import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * @dnd-kit sortable row, replacing react-beautiful-dnd's `<Draggable>`.
 *
 * Mirrors the old render-prop ergonomics: `children` is a function that receives
 * `{ dragHandleProps }` to spread onto the drag handle (or the whole row). The
 * outer wrapper holds the node ref + transform, so callers no longer wire refs.
 *
 *   <SortableItem id={item.id} disabled={readOnly}>
 *     {({ dragHandleProps }) => (
 *       <Row>
 *         <Handle {...dragHandleProps} />
 *       </Row>
 *     )}
 *   </SortableItem>
 *
 * `as="tr"` makes the row a table row, for a sortable `<tbody>`.
 */
export default function SortableItem({ id, disabled = false, className, as: Element = 'div', children, ...rest }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const dragHandleProps = { ...attributes, ...listeners };

  return (
    <Element ref={setNodeRef} style={style} className={className} {...rest}>
      {typeof children === 'function' ? children({ dragHandleProps }) : children}
    </Element>
  );
}
